import { useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Link2, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  PROGRAM_PAGE_LIMITS,
  type ProgramPageBlock,
  type ProgramPageProgram,
  type ProgramPageTierItem,
  type TiersBlock,
} from "@/types/programPage";
import { formatCommission, newId, newTierItem } from "@/components/programPage/templates";
import {
  ColorField,
  CtaField,
  Field,
  IconField,
  ImageField,
  LinesField,
  SegmentedField,
  TextAreaField,
  TextField,
  ToggleField,
} from "./fields";

const SELECT =
  "h-9 w-full rounded-lg border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

interface Props {
  block: ProgramPageBlock;
  programs: ProgramPageProgram[];
  onChange: (block: ProgramPageBlock) => void;
}

/** The form for one block. Each block type edits only its own fields. */
export function BlockInspector({ block, programs, onChange }: Props) {
  // `patch` is typed per-branch below; the cast keeps one helper for all nine.
  const patch = (changes: Partial<ProgramPageBlock>) =>
    onChange({ ...block, ...changes } as ProgramPageBlock);

  switch (block.type) {
    case "hero":
      return (
        <Stack>
          <SegmentedField
            label="Layout"
            value={block.layout}
            options={[
              { value: "centered", label: "Centered" },
              { value: "split", label: "Side by side" },
              { value: "cover", label: "Full image" },
            ]}
            onChange={(layout) => patch({ layout })}
          />
          <TextField label="Eyebrow" value={block.eyebrow} maxLength={60} onChange={(eyebrow) => patch({ eyebrow })} />
          <TextField label="Headline" value={block.title} maxLength={120} onChange={(title) => patch({ title })} />
          <TextAreaField label="Supporting line" value={block.subtitle} maxLength={400} onChange={(subtitle) => patch({ subtitle })} />
          <ImageField label="Image" value={block.imageUrl} onChange={(imageUrl) => patch({ imageUrl })} />
          <CtaField value={block.cta} onChange={(cta) => patch({ cta })} />
        </Stack>
      );

    case "tiers":
      return <TiersInspector block={block} programs={programs} onChange={onChange} />;

    case "features":
      return (
        <Stack>
          <TextField label="Heading" value={block.heading} maxLength={120} onChange={(heading) => patch({ heading })} />
          <TextAreaField label="Intro" value={block.subheading} maxLength={300} rows={2} onChange={(subheading) => patch({ subheading })} />
          <SegmentedField
            label="Columns"
            value={block.columns}
            options={[2, 3, 4].map((n) => ({ value: n as 2 | 3 | 4, label: String(n) }))}
            onChange={(columns) => patch({ columns })}
          />
          <ItemList
            items={block.items}
            noun="card"
            max={PROGRAM_PAGE_LIMITS.items}
            title={(item) => item.title}
            create={() => ({ id: newId(), icon: { kind: "lucide" as const, value: "Sparkles" }, title: "New card", text: "" })}
            onChange={(items) => patch({ items })}
            render={(item, update) => (
              <>
                <IconField label="Icon" value={item.icon} onChange={(icon) => update({ icon })} />
                <TextField label="Title" value={item.title} maxLength={80} onChange={(title) => update({ title })} />
                <TextAreaField label="Text" value={item.text} maxLength={400} onChange={(text) => update({ text })} />
              </>
            )}
          />
        </Stack>
      );

    case "steps":
      return (
        <Stack>
          <TextField label="Heading" value={block.heading} maxLength={120} onChange={(heading) => patch({ heading })} />
          <TextAreaField label="Intro" value={block.subheading} maxLength={300} rows={2} onChange={(subheading) => patch({ subheading })} />
          <ItemList
            items={block.items}
            noun="step"
            max={PROGRAM_PAGE_LIMITS.items}
            title={(item) => item.title}
            create={() => ({ id: newId(), title: "New step", text: "" })}
            onChange={(items) => patch({ items })}
            render={(item, update) => (
              <>
                <TextField label="Title" value={item.title} maxLength={100} onChange={(title) => update({ title })} />
                <TextAreaField label="Text" value={item.text} maxLength={400} onChange={(text) => update({ text })} />
              </>
            )}
          />
        </Stack>
      );

    case "stats":
      return (
        <ItemList
          items={block.items}
          noun="stat"
          max={6}
          title={(item) => [item.value, item.label].filter(Boolean).join(" · ")}
          create={() => ({ id: newId(), value: "100+", label: "Creators" })}
          onChange={(items) => patch({ items })}
          render={(item, update) => (
            <>
              <TextField label="Number" value={item.value} maxLength={16} onChange={(value) => update({ value })} />
              <TextField label="Label" value={item.label} maxLength={60} onChange={(label) => update({ label })} />
            </>
          )}
        />
      );

    case "text":
      return (
        <Stack>
          <TextField label="Heading" value={block.heading} maxLength={120} onChange={(heading) => patch({ heading })} />
          <TextAreaField label="Body" value={block.body} maxLength={4000} rows={8} onChange={(body) => patch({ body })} />
          <SegmentedField
            label="Alignment"
            value={block.align}
            options={[
              { value: "left", label: "Left" },
              { value: "center", label: "Centered" },
            ]}
            onChange={(align) => patch({ align })}
          />
        </Stack>
      );

    case "image":
      return (
        <Stack>
          <ImageField label="Image" value={block.imageUrl} onChange={(imageUrl) => patch({ imageUrl })} />
          <TextField
            label="Description"
            hint="Read aloud by screen readers."
            value={block.alt}
            maxLength={160}
            onChange={(alt) => patch({ alt })}
          />
          <TextField label="Caption" value={block.caption} maxLength={200} onChange={(caption) => patch({ caption })} />
        </Stack>
      );

    case "faq":
      return (
        <Stack>
          <TextField label="Heading" value={block.heading} maxLength={120} onChange={(heading) => patch({ heading })} />
          <ItemList
            items={block.items}
            noun="question"
            max={PROGRAM_PAGE_LIMITS.items}
            title={(item) => item.question}
            create={() => ({ id: newId(), question: "New question", answer: "" })}
            onChange={(items) => patch({ items })}
            render={(item, update) => (
              <>
                <TextField label="Question" value={item.question} maxLength={200} onChange={(question) => update({ question })} />
                <TextAreaField label="Answer" value={item.answer} maxLength={1200} rows={4} onChange={(answer) => update({ answer })} />
              </>
            )}
          />
        </Stack>
      );

    case "cta":
      return (
        <Stack>
          <TextField label="Headline" value={block.title} maxLength={120} onChange={(title) => patch({ title })} />
          <TextAreaField label="Supporting line" value={block.subtitle} maxLength={300} rows={2} onChange={(subtitle) => patch({ subtitle })} />
          <CtaField value={block.cta} onChange={(cta) => patch({ cta })} />
        </Stack>
      );
  }
}

function TiersInspector({
  block,
  programs,
  onChange,
}: {
  block: TiersBlock;
  programs: ProgramPageProgram[];
  onChange: (block: ProgramPageBlock) => void;
}) {
  const patch = (changes: Partial<TiersBlock>) => onChange({ ...block, ...changes });
  const program =
    programs.find((p) => p.id === block.programId) ?? programs.find((p) => p.isDefault) ?? programs[0] ?? null;
  const linked = new Set(block.items.map((item) => item.tierId).filter(Boolean));
  const unlinked = program?.tiers.filter((tier) => !linked.has(tier.id)) ?? [];

  return (
    <Stack>
      <TextField label="Heading" value={block.heading} maxLength={120} onChange={(heading) => patch({ heading })} />
      <TextAreaField label="Intro" value={block.subheading} maxLength={300} rows={2} onChange={(subheading) => patch({ subheading })} />
      <SegmentedField
        label="Layout"
        value={block.layout}
        options={[
          { value: "cards", label: "Cards" },
          { value: "ladder", label: "Ladder" },
          { value: "table", label: "Table" },
        ]}
        onChange={(layout) => patch({ layout })}
      />

      {programs.length > 1 && (
        <Field
          label="Program"
          hint="“Each affiliate's own” shows every affiliate the ladder of the program they are in."
        >
          <select
            className={SELECT}
            value={block.programId ?? ""}
            // Tier links belong to one program's ladder, so they reset with it.
            onChange={(e) =>
              patch({
                programId: e.target.value || null,
                items: block.items.map((item) => ({ ...item, tierId: null })),
              })
            }
          >
            <option value="">Each affiliate's own (default program for visitors)</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || "Untitled program"}
              </option>
            ))}
          </select>
        </Field>
      )}

      <ToggleField
        label="Show affiliates their progress"
        hint="Signed-in affiliates see where they stand and what's left to the next tier."
        checked={block.showProgress}
        onChange={(showProgress) => patch({ showProgress })}
      />
      <ToggleField
        label="Show sales thresholds"
        checked={block.showThresholds}
        onChange={(showThresholds) => patch({ showThresholds })}
      />

      {program && !program.tiers.length && (
        <p className="rounded-lg border border-border bg-muted p-3 text-xs text-muted-foreground">
          This program has no commission tiers yet, so these cards are display-only.{" "}
          <Link to={`/brand/programs/${program.id}`} className="font-medium text-brand-700 underline dark:text-brand-300">
            Set up tiers
          </Link>{" "}
          to show live rates and affiliate progress.
        </p>
      )}
      {unlinked.length > 0 && (
        <button
          type="button"
          disabled={block.items.length >= PROGRAM_PAGE_LIMITS.tierItems}
          onClick={() =>
            patch({
              items: [
                ...block.items,
                ...unlinked
                  .slice(0, PROGRAM_PAGE_LIMITS.tierItems - block.items.length)
                  .map((tier) => newTierItem({ tierId: tier.id })),
              ],
            })
          }
          className="inline-flex items-center gap-1.5 self-start text-xs font-medium text-brand-700 hover:underline disabled:opacity-50 dark:text-brand-300"
        >
          <Link2 className="h-3.5 w-3.5" />
          Add cards for {unlinked.length} tier{unlinked.length === 1 ? "" : "s"} not on the page yet
        </button>
      )}

      <ItemList<ProgramPageTierItem>
        items={block.items}
        noun="tier"
        max={PROGRAM_PAGE_LIMITS.tierItems}
        title={(item) => item.name || program?.tiers.find((t) => t.id === item.tierId)?.name || "Untitled tier"}
        create={() => newTierItem({ name: "New tier" })}
        onChange={(items) => patch({ items })}
        render={(item, update) => {
          const tier = program?.tiers.find((t) => t.id === item.tierId) ?? null;
          return (
            <>
              {!!program?.tiers.length && (
                <Field
                  label="Linked commission tier"
                  hint={
                    tier
                      ? "Name and rate follow this tier, and affiliates see their progress to it."
                      : "Display-only: nothing is tracked. Good for invite-only ranks."
                  }
                >
                  <select
                    className={SELECT}
                    value={item.tierId ?? ""}
                    onChange={(e) => update({ tierId: e.target.value || null })}
                  >
                    <option value="">Not linked</option>
                    {program.tiers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} · {formatCommission(program, t.commissionValue)}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <TextField
                label="Name"
                value={item.name}
                maxLength={40}
                placeholder={tier?.name}
                hint={tier ? "Leave empty to use the tier's own name." : undefined}
                onChange={(name) => update({ name })}
              />
              <TextField
                label="Commission shown"
                value={item.commissionLabel}
                maxLength={24}
                placeholder={tier && program ? formatCommission(program, tier.commissionValue) : "e.g. 25%"}
                hint={tier ? "Leave empty to show the live rate." : undefined}
                onChange={(commissionLabel) => update({ commissionLabel })}
              />
              <TextField label="Badge" value={item.badge} maxLength={24} placeholder="e.g. Invite only" onChange={(badge) => update({ badge })} />
              <TextField label="Tagline" value={item.tagline} maxLength={160} onChange={(tagline) => update({ tagline })} />
              <div className="grid grid-cols-2 gap-3">
                <IconField label="Icon" value={item.icon} onChange={(icon) => update({ icon })} />
                <ColorField label="Colour" value={item.color} onChange={(color) => update({ color })} onClear={() => update({ color: null })} />
              </div>
              <ImageField label="Card image" value={item.imageUrl} onChange={(imageUrl) => update({ imageUrl })} hint="Optional. Shown across the top of the card." />
              <TextField label="First list title" value={item.requirementsTitle} maxLength={40} onChange={(requirementsTitle) => update({ requirementsTitle })} />
              <LinesField
                label="First list"
                value={item.requirements}
                maxLines={PROGRAM_PAGE_LIMITS.listLines}
                placeholder={"6+ orders with your code\n4 Reels\n8 Stories"}
                onChange={(requirements) => update({ requirements })}
              />
              <TextField label="Second list title" value={item.rewardsTitle} maxLength={40} onChange={(rewardsTitle) => update({ rewardsTitle })} />
              <LinesField
                label="Second list"
                value={item.rewards}
                maxLines={PROGRAM_PAGE_LIMITS.listLines}
                placeholder={"15% commission\n2 free items\nEarly access to drops"}
                onChange={(rewards) => update({ rewards })}
              />
            </>
          );
        }}
      />
    </Stack>
  );
}

function Stack({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>;
}

/** An accordion of a block's repeatable entries: add, reorder, remove, edit. */
function ItemList<T extends { id: string }>({
  items,
  noun,
  max,
  title,
  create,
  onChange,
  render,
}: {
  items: T[];
  noun: string;
  max: number;
  title: (item: T) => string;
  create: () => T;
  onChange: (items: T[]) => void;
  render: (item: T, update: (changes: Partial<T>) => void) => ReactNode;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  const move = (index: number, by: -1 | 1) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(index + by, 0, item);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => {
        const open = openId === item.id;
        return (
          <div key={item.id} className="rounded-lg border border-border">
            <div className="flex items-center gap-1 px-2 py-1.5">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : item.id)}
                className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left text-sm font-medium"
              >
                <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                <span className="truncate">{title(item) || `Untitled ${noun}`}</span>
              </button>
              <IconButton label={`Move ${noun} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                <ArrowUp className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton label={`Move ${noun} down`} disabled={index === items.length - 1} onClick={() => move(index, 1)}>
                <ArrowDown className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton label={`Remove ${noun}`} danger onClick={() => onChange(items.filter((i) => i.id !== item.id))}>
                <Trash2 className="h-3.5 w-3.5" />
              </IconButton>
            </div>
            {open && (
              <div className="flex flex-col gap-3 border-t border-border p-3">
                {render(item, (changes) =>
                  onChange(items.map((i) => (i.id === item.id ? { ...i, ...changes } : i)))
                )}
              </div>
            )}
          </div>
        );
      })}
      {items.length < max && (
        <button
          type="button"
          onClick={() => {
            const item = create();
            onChange([...items, item]);
            setOpenId(item.id);
          }}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2 text-xs font-medium text-muted-foreground hover:border-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
        >
          <Plus className="h-3.5 w-3.5" /> Add {noun}
        </button>
      )}
    </div>
  );
}

function IconButton({
  label,
  danger = false,
  disabled,
  onClick,
  children,
}: {
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`rounded-md p-1.5 text-muted-foreground disabled:opacity-30 ${
        danger ? "hover:text-destructive" : "hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
